/**
 * A aba "Roteamento" de Etapas do funil: para qual funil vai cada número.
 *
 * ── O que ela decide ─────────────────────────────────────────────────────
 *
 * Onde nasce a oportunidade que o sistema cria sozinho quando chega conversa
 * por aquele número ou canal. Caixa sem regra vai para o funil padrão, e a
 * tela diz isso na própria opção, para ninguém achar que "sem regra" é "não
 * cria".
 *
 * ── As automações (056) ──────────────────────────────────────────────────
 *
 * Responsável, etiqueta e tarefa de retorno valem quando a regra CRIA a
 * oportunidade. Só aparecem com um funil escolhido: sem regra não há quem as
 * aplique, e campos que não fazem nada seriam promessa falsa.
 *
 * ── Uma linha por caixa, e não uma lista de caixas por funil ─────────────
 *
 * Um funil recebe quantos números quiser, mas cada número vai para um funil
 * só. Com a caixa na linha e o funil no seletor, escolher dois funis para o
 * mesmo número simplesmente não é possível, e ninguém precisa ler uma regra
 * para saber disso.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CaixaRoteada, Etiqueta, EtapaDoFunil, Funil, Pessoa } from '@cliqcrm/contracts';
import { useApi, useCatalogoDeEtiquetas, usePessoas } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, CampoSelecao, Cartao } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { nomeDaEtapa } from '@/entities/comercial/etapa';

/** O canal como quem usa o produto o chama. */
const NOME_DO_CANAL: Readonly<Record<string, string>> = {
  whatsapp_cloud: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
  telegram: 'Telegram',
  web_widget: 'Chat do site',
  api: 'Integração',
};

/** Os prazos oferecidos para a tarefa de retorno, em horas. */
const PRAZOS_DA_TAREFA: readonly { horas: number; rotulo: string }[] = [
  { horas: 1, rotulo: 'Em 1 hora' },
  { horas: 2, rotulo: 'Em 2 horas' },
  { horas: 4, rotulo: 'Em 4 horas' },
  { horas: 24, rotulo: 'Em 1 dia' },
  { horas: 48, rotulo: 'Em 2 dias' },
];

export function RoteamentoDeFunil({
  funis,
  etapas,
}: {
  funis: readonly Funil[];
  etapas: readonly EtapaDoFunil[];
}): ReactNode {
  const api = useApi();
  const roteamento = useQuery({
    queryKey: chaves.roteamentoDeFunil(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.roteamentoDeFunil({ sinal: signal }),
  });
  const pessoas = usePessoas();
  const etiquetas = useCatalogoDeEtiquetas();

  /*
   * Só quem pode RECEBER: ativo e com cadastro no CRM. Sem o cadastro, a
   * oportunidade não teria como ficar com a pessoa, e o servidor recusaria.
   */
  const responsaveis = (pessoas.data?.itens ?? []).filter(
    (p) => p.status === 'ativo' && p.vinculoNoCrm === 'ligado',
  );

  return (
    <div role="tabpanel" aria-label="Roteamento" className="admin__funis-painel">
      <p className="admin__nota">
        Escolha para qual funil vai a oportunidade criada automaticamente quando chega conversa por
        cada número ou canal. O que ficar sem regra vai para o funil padrão.
      </p>

      {roteamento.isPending && <p>Buscando os números conectados…</p>}
      {roteamento.isError && (
        <Faixa tom="bloqueado">Não foi possível buscar os números conectados.</Faixa>
      )}
      {roteamento.data?.itens.length === 0 && (
        <p className="admin__nota">
          Nenhum número ou canal conectado ainda. Conecte um em Conexões e ele aparece aqui.
        </p>
      )}

      {roteamento.data?.itens.map((caixa) => (
        <LinhaDaCaixa
          key={caixa.caixa}
          caixa={caixa}
          funis={funis}
          etapas={etapas}
          responsaveis={responsaveis}
          etiquetas={etiquetas.data?.itens ?? []}
        />
      ))}
    </div>
  );
}

function LinhaDaCaixa({
  caixa,
  funis,
  etapas,
  responsaveis,
  etiquetas,
}: {
  caixa: CaixaRoteada;
  funis: readonly Funil[];
  etapas: readonly EtapaDoFunil[];
  responsaveis: readonly Pessoa[];
  etiquetas: readonly Etiqueta[];
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [funil, definirFunil] = useState(caixa.regra?.funil ?? '');
  const [etapa, definirEtapa] = useState(caixa.regra?.etapa_inicial ?? '');
  const [responsavel, definirResponsavel] = useState(caixa.regra?.responsavel_usuario_id ?? '');
  const [etiqueta, definirEtiqueta] = useState(caixa.regra?.etiqueta ?? '');
  const [prazo, definirPrazo] = useState(String(caixa.regra?.tarefa_em_horas ?? ''));
  const [recado, definirRecado] = useState<{ ok: boolean; texto: string } | null>(null);

  const mudou =
    funil !== (caixa.regra?.funil ?? '') ||
    etapa !== (caixa.regra?.etapa_inicial ?? '') ||
    responsavel !== (caixa.regra?.responsavel_usuario_id ?? '') ||
    etiqueta !== (caixa.regra?.etiqueta ?? '') ||
    prazo !== String(caixa.regra?.tarefa_em_horas ?? '');

  /*
   * Só as etapas ABERTAS do funil escolhido: etapa final não serve de entrada
   * (a oportunidade nasceria encerrada), e o servidor recusaria.
   */
  const etapasDoFunil = etapas
    .filter((e) => e.funil === funil && e.aberta)
    .sort((a, b) => a.posicao - b.posicao);

  const salvar = useMutation({
    /*
     * "Sem regra" é TIRAR a regra. Escolher o funil padrão de propósito é
     * outra coisa: é uma regra, e é o que permite dar responsável, etiqueta e
     * tarefa ao número que cai no padrão.
     */
    mutationFn: async (): Promise<void> => {
      if (funil === '') {
        await api!.removerRoteamentoDeFunil({ caminho: { caixa: caixa.caixa } });
        return;
      }
      await api!.definirRoteamentoDeFunil({
        caminho: { caixa: caixa.caixa },
        corpo: {
          funil,
          etapa_inicial: etapa === '' ? null : etapa,
          responsavel_usuario_id: responsavel === '' ? null : responsavel,
          etiqueta: etiqueta === '' ? null : etiqueta,
          tarefa_em_horas: prazo === '' ? null : Number(prazo),
        },
      });
    },
    onMutate: () => definirRecado(null),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: chaves.roteamentoDeFunil() });
      const nome = funis.find((f) => f.chave === funil)?.nome;
      definirRecado({
        ok: true,
        texto: nome
          ? `Pronto. As próximas oportunidades deste número nascem no funil ${nome}.`
          : 'Pronto. As próximas oportunidades deste número nascem no funil padrão.',
      });
    },
    onError: (erro) =>
      definirRecado({
        ok: false,
        texto: recadoDeErro(erro, { generica: 'Não foi possível salvar agora.' }),
      }),
  });

  const nomeDoCanal = NOME_DO_CANAL[caixa.canal] ?? caixa.canal;

  return (
    <Cartao
      como="section"
      variante="folgado"
      parte="admin__rota"
      aria-label={`Roteamento de ${caixa.nome}`}
    >
      <div className="admin__rota-caixa">
        <strong>{caixa.nome}</strong>
        <span className="admin__nota">{nomeDoCanal}</span>
      </div>

      <CampoSelecao
        rotulo="Vai para o funil"
        value={funil}
        onChange={(e) => {
          definirFunil(e.currentTarget.value);
          // A etapa era do funil de antes.
          definirEtapa('');
        }}
      >
        <option value="">Sem regra (vai para o funil padrão)</option>
        {funis.map((f) => (
          <option key={f.chave} value={f.chave}>
            {f.nome}
          </option>
        ))}
      </CampoSelecao>

      <CampoSelecao
        rotulo="Entra na etapa"
        value={etapa}
        disabled={funil === ''}
        onChange={(e) => definirEtapa(e.currentTarget.value)}
      >
        <option value="">Primeira etapa do funil</option>
        {etapasDoFunil.map((e) => (
          <option key={e.valor} value={e.valor}>
            {nomeDaEtapa(e.rotulo)}
          </option>
        ))}
      </CampoSelecao>

      {funil === '' ? (
        <p className="admin__nota">
          Escolha um funil para definir responsável, etiqueta e tarefa de retorno deste número.
        </p>
      ) : (
        <fieldset className="admin__rota-automacoes">
          <legend>Quando a oportunidade nascer por aqui</legend>

          <CampoSelecao
            rotulo="Responsável"
            value={responsavel}
            onChange={(e) => definirResponsavel(e.currentTarget.value)}
          >
            <option value="">Ninguém (fica para a equipe)</option>
            {responsaveis.map((p) => (
              <option key={p.usuarioId} value={p.usuarioId}>
                {p.nome}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao
            rotulo="Etiqueta na conversa"
            value={etiqueta}
            onChange={(e) => definirEtiqueta(e.currentTarget.value)}
          >
            <option value="">Nenhuma</option>
            {etiquetas.map((t) => (
              <option key={t.titulo} value={t.titulo}>
                {t.titulo}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao
            rotulo="Tarefa de retorno"
            value={prazo}
            onChange={(e) => definirPrazo(e.currentTarget.value)}
          >
            <option value="">Não criar</option>
            {PRAZOS_DA_TAREFA.map((p) => (
              <option key={p.horas} value={String(p.horas)}>
                {p.rotulo}
              </option>
            ))}
          </CampoSelecao>
        </fieldset>
      )}

      <div className="admin__form-acoes">
        <Botao
          miudo
          desabilitado={!mudou || salvar.isPending || api === null}
          motivo={
            salvar.isPending
              ? 'Salvando…'
              : api === null
                ? 'Sem conexão agora. Tente de novo em instantes.'
                : 'Nada mudou desde a última vez que foi salvo.'
          }
          onClick={() => salvar.mutate()}
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </Botao>
      </div>

      {recado && (
        <p
          className={recado.ok ? 'admin__nota admin__nota--ok' : 'admin__nota'}
          role={recado.ok ? 'status' : 'alert'}
        >
          {recado.texto}
        </p>
      )}
    </Cartao>
  );
}
